'use client'

import { useEffect, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { LuFilter } from 'react-icons/lu'
import { fetchBounties, BountyListItem, BountyMeta } from '../actions'
import BountyList from './BountyList'
import BountySort from './BountySort'

export default function BountyListingContainer() {
  const searchParams = useSearchParams()
  const [bounties, setBounties] = useState<BountyListItem[]>([])
  const [meta, setMeta] = useState<BountyMeta | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [filterOpen, setFilterOpen] = useState(false)
  const [minRewardFilter, setMinRewardFilter] = useState<number | null>(null)

  useEffect(() => {
    setIsLoading(true)
    const currentPage = searchParams.get('page')

    const keyword = searchParams.get('keyword') || ''
    const sort = searchParams.get('sort') || 'newest'
    const page = Number(currentPage) || 1
    const state = searchParams.get('state') || 'register_now'

    fetchBounties(keyword, sort, page, state)
      .then((res) => {
        setBounties((prev) =>
          currentPage && currentPage !== '1' ? [...prev, ...res.data] : res.data
        )
        setMeta(res.meta)
      })
      .catch((err) => {
        console.error('Failed to fetch bounties:', err)
        setBounties([])
      })
      .finally(() => setIsLoading(false))
  }, [searchParams])

  const handleLoadMore = () => {
    const params = new URLSearchParams(searchParams)
    const nextPage = (Number(params.get('page')) || 1) + 1
    params.set('page', String(nextPage))
    window.history.replaceState(null, '', `?${params.toString()}`)
  }

  const displayedBounties =
    minRewardFilter != null
      ? bounties.filter(
          (b) => b.rewardMax == null || b.rewardMax >= minRewardFilter
        )
      : bounties

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-4">
          <button
            type="button"
            onClick={() => setFilterOpen((prev) => !prev)}
            className={`flex items-center gap-3 border text-sm px-5 py-2.5 rounded-lg bg-white transition-colors cursor-pointer ${
              filterOpen || minRewardFilter != null
                ? 'border-gray-800 text-gray-900'
                : 'border-gray-200 text-gray-600 hover:border-gray-300'
            }`}
          >
            <LuFilter className="h-5 w-5" />
            <span className="text-lg">
              Filter{minRewardFilter != null ? ' (1)' : ''}
            </span>
          </button>
          {meta && meta.total !== 0 ? (
            <span>
              {meta.total} bug {meta.total === 1 ? 'bounty' : 'bounties'} found
            </span>
          ) : (
            <span>No Matching Results</span>
          )}
        </div>
        <BountySort />
      </div>

      {filterOpen && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 flex flex-wrap gap-4 items-end">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="min-reward-filter"
              className="text-xs font-medium text-gray-700"
            >
              Min Reward ($)
            </label>
            <input
              id="min-reward-filter"
              type="number"
              min={0}
              placeholder="e.g. 1000"
              value={minRewardFilter ?? ''}
              onChange={(e) =>
                setMinRewardFilter(
                  e.target.value ? Number(e.target.value) : null
                )
              }
              className="w-36 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <button
            type="button"
            onClick={() => {
              setMinRewardFilter(null)
              setFilterOpen(false)
            }}
            className="text-sm text-gray-500 hover:text-gray-700 underline pb-2 cursor-pointer"
          >
            Clear filters
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-gray-800" />
        </div>
      ) : (
        <BountyList
          bounties={displayedBounties}
          meta={meta}
          onLoadMore={handleLoadMore}
        />
      )}
    </>
  )
}
